import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';
import ReactDOMServer from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {DEEP_RESEARCH_ANALYTICS_ACTIONS} from 'constants/analytics-products/DeepResearchAnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import EntryDAO from 'dao/EntryDAO';
import {ProgressData} from 'llm/deep-research/messages/DeepResearchProgress';
import {convertMarkdownToWord} from 'llm/deep-research/messages/MarkdownToWordConverter';
import {
  SaveAsResearchReportModal,
  SaveAsResearchReportResult,
} from 'llm/deep-research/SaveAsResearchReportModal';
import {rehypeBenchlingTagsForSave} from 'llm/markdown/LLMMarkdownRehype';
import {remarkBenchlingTags} from 'llm/markdown/LLMMarkdownRemark';
import LLMMarkdownSection, {
  createBenchlingTagConfigs,
  createSanitizeSchema,
  SaveToEntryOption,
} from 'llm/markdown/LLMMarkdownSection';
import {BenchlingTagConfigs, cleanMarkdownForSaving} from 'llm/markdown/LLMMarkdownUtils';
import {remarkGfmTables} from 'llm/markdown/remarkTables';
import CitationSourceChip, {WebCitationSource} from 'llm/markdown/SourceChip';
import {AddToEntryModal} from 'llm/modals/AddToEntryModal';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {WYCIWYPPart} from 'notebook/mediocre/clipboard/WyciwypConstants';
import * as TableMapUtil from 'notebook/mediocre/util/TableMapUtil';
import {makeDefaultItem, makeItemFromWyciwypPart} from 'notebook/notes/CreateOps';
import EntryUtil from 'notebook/util/EntryUtil';
import {NotePart, NotePartType} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';
import {sanitizeName} from 'util/FileUtil';
import * as logging from 'util/Logging';
import {initiateFileDownload} from 'util/TextToBlobUtil';

import {useActivityModalControls} from '../ActivityModal';

export async function getUsedTableNames(entryId: string): Promise<Array<string>> {
  const entryData = await EntryDAO.get(entryId);
  const entry = EntryUtil.prepareForStore(entryData!, {});
  return Object.keys(TableMapUtil.getNoteTableMap(entryId, entry.note));
}

/**
 * Converts LLM markdown (with custom Benchling tags) into note parts for a notebook entry.
 * - Removes UI-only tags, preserving only tags needed for entry-saving.
 * - Renders markdown to sanitized HTML, then parses to note parts.
 * - Returns client-shaped NotePart data; the DAO layer will transform to server format.
 */
export function convertMarkdownToNoteParts(
  markdownContent: string,
  options: {usedTableNames: Array<string>}
): Array<NotePart> {
  // Clean the markdown content by removing tags that shouldn't be kept when saving
  const tagConfigs = createBenchlingTagConfigs({});

  // Clean the markdown content, removing tags that should not be processed (such as citations)
  const cleanedMarkdown = cleanMarkdownForSaving(markdownContent, tagConfigs);

  // Special rendering for the components of custom tags that need to be saved
  const saveComponents = Object.fromEntries(
    tagConfigs.tagConfigs
      .filter((config) => config.preserveContentWhenSaving === SaveToEntryOption.PRESERVE_TAGS)
      .map((config) => [config.htmlTagName, config.savingComponent])
  );

  // Create config for sanitization keeping only tags that need to be preserved
  const sanitizationTagConfigs: BenchlingTagConfigs = {
    tagConfigs: tagConfigs.tagConfigs.filter(
      (config) => config.preserveContentWhenSaving === SaveToEntryOption.PRESERVE_TAGS
    ),
  };
  const sanitizationSchema = createSanitizeSchema(sanitizationTagConfigs);

  const htmlContent = ReactDOMServer.renderToStaticMarkup(
    <ReactMarkdown
      remarkPlugins={[[remarkBenchlingTags, tagConfigs], remarkGfmTables]}
      rehypePlugins={[
        [rehypeBenchlingTagsForSave, tagConfigs],
        [rehypeSanitize, sanitizationSchema],
      ]}
      components={saveComponents}
    >
      {cleanedMarkdown}
    </ReactMarkdown>
  );

  // Convert markdown to noteparts using the existing wyciwyp system
  const mockClipboardData = {
    types: ['text/html', 'text/plain'],
    getData: (type: string) => {
      if (type === 'text/html') {
        return htmlContent;
      }
      if (type === 'text/plain') {
        return cleanedMarkdown;
      }
      return '';
    },
  } as unknown as DataTransfer;

  const wyciwypParts = wyciwyp.processClipboard(mockClipboardData, {
    allowTables: true,
    allowHeaders: true,
    allowLinks: false,
  });

  const {usedTableNames} = options;

  // Convert wyciwyp parts to proper initial note parts using the existing utility
  const noteparts = wyciwypParts.map((part: WYCIWYPPart) => {
    const {item} = makeItemFromWyciwypPart(part, usedTableNames, true);
    const notePartBase = makeDefaultItem(item.type, null, {initialNotePart: false});
    let notePart;
    switch (item.type) {
      case NotePartType.TABLE:
        assert(item.table != null, 'Expected table to be present when item type is TABLE', {
          itemType: item.type,
        });
        notePart = {
          ...notePartBase,
          table: item.table,
          indentation: item.indentation,
          headerType: null,
        };
        break;
      case NotePartType.TEXT:
      case NotePartType.CODE:
        notePart = {
          ...notePartBase,
          text: item.text,
          indentation: item.indentation,
          listType: item.listType,
          formats: item.formats!,
          headerType: item.headerType,
        };
        break;
      default:
        throw new Error(`Unsupported note part type from wyciwyp: ${item.type}`);
    }
    return notePart;
  });

  return noteparts;
}

interface CitationMaps {
  toolCitationMap: Map<string, number>;
  webCitations: Set<string>;
}

const createCitationMaps = (markdown: string): CitationMaps => {
  // Extract tool citations
  const toolCitationRegex = /<bnch_citation>(.*?)<\/bnch_citation>/g;
  const toolCitationIds = Array.from(
    new Set(Array.from(markdown.matchAll(toolCitationRegex), (match) => match[1]))
  );

  const toolCitationMap = new Map<string, number>();
  toolCitationIds.forEach((id, index) => {
    toolCitationMap.set(id, index + 1);
  });

  // Extract web citations
  const webCitationRegex = /<web_citation>(.*?)<\/web_citation>/g;
  const webCitationUrls = Array.from(
    new Set(Array.from(markdown.matchAll(webCitationRegex), (match) => match[1]))
  );
  const webCitations = new Set(webCitationUrls);

  return {toolCitationMap, webCitations};
};

export interface ReportMessageContentsProps {
  report: string;
  onCitationClick?: (toolId: string) => void;
  researchActivity?: ProgressData | null;
  readonly?: boolean;
  chatSessionId: string;
}

export function DeepResearchReportMessageContents({
  report,
  researchActivity,
  readonly,
  chatSessionId,
}: ReportMessageContentsProps): JSX.Element {
  const {openActivityModal} = useActivityModalControls();
  const {toolCitationMap, webCitations} = createCitationMaps(report);

  const onCitationClick = useCallback(
    (citation: string, isSourceCitation = false) => {
      const toolType = researchActivity?.historyThread.find((tool) => tool.toolId === citation)?.tool;

      trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.CLICKED_DEEP_RESEARCH_CITATION, {
        chat_session_id: chatSessionId,
        is_source_citation: isSourceCitation,
        tool_type: toolType,
      });

      if (!researchActivity) {
        if (readonly) {
          // TODO: No-op'ing on click is not a great UX, but if a chat is being viewed via a share link,
          // it is expected we won't have the data to open the activity modal.
          return;
        }
        logging.error('[llm] Citation clicked but no progress data available', {
          extra: {citation},
        });
        return;
      }
      openActivityModal(researchActivity, citation);
    },
    [researchActivity, openActivityModal, readonly, chatSessionId]
  );
  const hasWebCitations = webCitations.size > 0;
  const hasToolCitations = toolCitationMap.size > 0;
  return (
    <>
      <LLMMarkdownSection
        markdown={report}
        onCitationClick={onCitationClick}
        progressData={researchActivity || undefined}
        citationNumberMap={toolCitationMap}
        chatSessionId={chatSessionId}
      />
      {(hasToolCitations || hasWebCitations) && (
        <div className={css(styles.sourcesSection)}>
          <h4>Sources</h4>
          {hasToolCitations && (
            <>
              {hasWebCitations && <h5>Benchling Citations</h5>}
              <ol className={css(styles.sourcesContainer)}>
                {Array.from(toolCitationMap.entries()).map(([citationId, citationNumber]) => (
                  <CitationSourceChip
                    key={citationId}
                    citationId={citationId}
                    citationNumber={citationNumber}
                    onCitationClick={onCitationClick}
                    toolCall={researchActivity?.historyThread.find((tool) => tool.toolId === citationId)}
                  />
                ))}
              </ol>
            </>
          )}
          {hasWebCitations && (
            <>
              {hasToolCitations && <h5>Web Citations</h5>}
              <ol className={css(styles.sourcesContainer)}>
                {Array.from(webCitations).map((url) => (
                  <WebCitationSource key={url} url={url} />
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </>
  );
}

export function SaveDeepResearchReportButton({
  markdownReport,
  chatSessionId,
}: {
  markdownReport: string;
  chatSessionId: string;
}): JSX.Element {
  const handleSaveClick = (targetType: string): void => {
    // When we get a context with a chat ID, we can add it here to the metadata
    const noteParts = convertMarkdownToNoteParts(markdownReport, {usedTableNames: []});
    trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.SAVED_DEEP_RESEARCH_TO_ENTRY, {
      targetType,
    });

    runModal(SaveAsResearchReportModal, {
      targetType,
      chatSessionId,
      initialNoteParts: noteParts,
      initialName: 'Deep Research Report',
      onRequestSubmit: (result: SaveAsResearchReportResult) => {
        const targetName = result.targetType === ModelTypeConstants.ENTRY ? 'Entry' : 'Template';
        notifications.success(
          `${targetName} created and research report content has been automatically added!`
        );
      },
    });
  };

  const handleAddToEntryClick = (): void => {
    trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.SAVED_DEEP_RESEARCH_TO_ENTRY);
    runModal(AddToEntryModal, {
      notePartsContentSourceId: chatSessionId,
      getContentsToInsert: async (entryId) => {
        const noteParts = convertMarkdownToNoteParts(markdownReport, {
          usedTableNames: await getUsedTableNames(entryId),
        });
        return {noteParts};
      },
      onRequestSubmit: (result) => {
        notifications.success(
          'Research report content has been added to the selected entry! Navigating to entry...'
        );
      },
    });
  };

  const handleDownloadAsWord = async (): Promise<void> => {
    trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.SAVED_DEEP_RESEARCH_TO_ENTRY, {
      targetType: 'word_document',
    });

    const blob = await convertMarkdownToWord(markdownReport);

    // Extract the first header from the markdown as a filename
    const firstHeaderMatch = markdownReport.match(/^#\s+(.+)$/m);
    const headerText = firstHeaderMatch?.[1] || 'Deep Research Report';

    const filename = `${sanitizeName(headerText)}.docx`;

    initiateFileDownload(filename, URL.createObjectURL(blob));

    notifications.success('Research report has been downloaded as a Word document!');
  };

  const saveDropdownMenu = (
    <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.MEDIUM} className="dropdown-menu pull-right">
      {BenchlingConfig.ELN_ENABLE_BLANK_ENTRY_CREATION ? (
        <Option
          value={ModelTypeConstants.ENTRY}
          onSelect={() => {
            handleSaveClick(ModelTypeConstants.ENTRY);
          }}
          icon="entry"
        >
          Save as Entry
        </Option>
      ) : null}
      <Option
        value={ModelTypeConstants.TEMPLATE}
        onSelect={() => {
          handleSaveClick(ModelTypeConstants.TEMPLATE);
        }}
        icon="template"
      >
        Save as Template
      </Option>
      <Option value="add-to-entry" onSelect={handleAddToEntryClick} icon="plus">
        Add to Existing Entry
      </Option>
      <Option value="download-word" onSelect={handleDownloadAsWord} icon="download">
        Save as Word
      </Option>
    </Menu>
  );

  return (
    <Dropdown menu={saveDropdownMenu} className="u-display-inline-block dropdown">
      <Button buttonStyle={ButtonStyle.PRIMARY} size={ButtonSize.SMALL} text="Save as..." isDropdown={true} />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  sourcesSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingBottom: '16px',
  },
  sourcesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    padding: '0px',
  },
});
